/**
 * Self-service password change dialog (triggered from header user menu).
 * Uses /api/auth/change-password.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Check, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const RULES = [
  { rule: /.{10,}/, label: 'At least 10 characters' },
  { rule: /[a-z]/, label: 'Lowercase letter' },
  { rule: /[A-Z]/, label: 'Uppercase letter' },
  { rule: /\d/, label: 'Digit' },
  { rule: /[!@#$%^&*()\-_=+\[\]{};:,.<>?/|~`]/, label: 'Special character' },
];

const ChangePasswordDialog = ({ open, onClose }) => {
  const { changePassword } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setCurrent(''); setNext(''); setConfirm(''); setErr(''); }
  }, [open]);

  const rules = RULES.map((r) => ({ ...r, ok: r.rule.test(next) }));
  const allOk = rules.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (next !== confirm) { setErr('Passwords do not match'); return; }
    if (!allOk) { setErr('Password does not meet policy'); return; }
    setSubmitting(true);
    const res = await changePassword(current, next);
    setSubmitting(false);
    if (res.success) {
      toast.success('Password updated — other devices have been signed out');
      onClose();
    } else {
      setErr(res.error || 'Update failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" />Change password</DialogTitle>
          <DialogDescription>Choose a strong password you don't reuse elsewhere.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Current password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="cp-current" autoComplete="current-password" />
          </div>
          <div>
            <Label>New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required data-testid="cp-new" autoComplete="new-password" />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="cp-confirm" autoComplete="new-password" />
          </div>
          <ul className="text-xs space-y-1 bg-muted/40 p-3 rounded">
            {rules.map((r) => (
              <li key={r.label} className={`flex items-center gap-2 ${r.ok ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'}`}>
                <Check className={`h-3 w-3 ${r.ok ? 'opacity-100' : 'opacity-30'}`} />
                {r.label}
              </li>
            ))}
          </ul>
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="cp-submit">
              {submitting ? 'Updating…' : 'Update'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
